import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Input } from '@foundation/components/ui/input';
import { Badge } from '@foundation/components/ui/badge';
import { useUser } from '@foundation/contextapi/UserContext';
import { useWorkspaceContext } from '@foundation/hooks/useWorkspaceContext';
import { getTemplates } from '../../services/policyGenerationService';
import type { PolicyGenerationTemplate } from '../../types/policyGeneration';
import {
  Search,
  FileText,
  FileSpreadsheet,
  FileType,
  Loader2,
  ChevronDown,
  Check,
  Library,
  X,
} from 'lucide-react';
import { Link } from 'react-router-dom';

type TemplateSelectorProps = {
  value?: PolicyGenerationTemplate | null;
  onChange: (template: PolicyGenerationTemplate | null) => void;
  organizationId?: string;
  userId?: string;
  placeholder?: string;
  /** Pre-fetched templates from the parent (e.g. PolicyDetailsPage). When
   *  provided the component skips its own internal fetch and uses these. */
  preloadedTemplates?: PolicyGenerationTemplate[];
  /** Loading state from the parent while it fetches preloaded templates. */
  isExternalLoading?: boolean;
};

const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  value,
  onChange,
  organizationId,
  userId: userIdProp,
  placeholder = 'Search and select a template...',
  preloadedTemplates,
  isExternalLoading = false,
}) => {
  const { user } = useUser();
  const { currentWorkspace } = useWorkspaceContext();

  const orgId = organizationId || currentWorkspace?.id || '';
  const userId = userIdProp || user?.id || '';

  const [templates, setTemplates] = useState<PolicyGenerationTemplate[]>([]);
  const [filteredTemplates, setFilteredTemplates] = useState<PolicyGenerationTemplate[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync preloaded templates into local state when provided by parent
  useEffect(() => {
    if (preloadedTemplates !== undefined) {
      setTemplates(preloadedTemplates);
      setFilteredTemplates(preloadedTemplates);
    }
  }, [preloadedTemplates]);

  // Internal fetch — only runs when no preloaded templates are provided
  const loadTemplates = useCallback(async () => {
    if (preloadedTemplates !== undefined) return; // parent is handling fetch
    if (!orgId || !userId) return;

    try {
      setIsLoading(true);
      setError(null);
      const data = await getTemplates(orgId, userId);
      setTemplates(data);
      setFilteredTemplates(data);
    } catch (err) {
      console.error('Error loading templates:', err);
      setError('Failed to load templates');
    } finally {
      setIsLoading(false);
    }
  }, [orgId, userId, preloadedTemplates]);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  // Resolved loading state: use parent's loading flag when templates are preloaded
  const showLoading = preloadedTemplates !== undefined ? isExternalLoading : isLoading;

  // Filter templates when search changes
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredTemplates(templates);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = templates.filter((t) =>
      t.doc_name.toLowerCase().includes(query)
    );
    setFilteredTemplates(filtered);
  }, [searchQuery, templates]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (template: PolicyGenerationTemplate) => {
    onChange(template);
    setIsOpen(false);
    setSearchQuery('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
    setSearchQuery('');
  };

  const getFileTypeIcon = (template: PolicyGenerationTemplate) => {
    const fileType = (template.metadata?.file_type as string) || (template.metadata?.fileType as string) || template.file_type || '';
    const lower = fileType.toLowerCase();

    if (lower.includes('pdf')) {
      return <FileText className="h-4 w-4 text-red-500" />;
    }
    if (lower.includes('doc') || lower.includes('docx')) {
      return <FileText className="h-4 w-4 text-blue-500" />;
    }
    if (lower.includes('xls') || lower.includes('xlsx') || lower.includes('csv')) {
      return <FileSpreadsheet className="h-4 w-4 text-green-500" />;
    }
    return <FileType className="h-4 w-4 text-gray-400" />;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Selected value or search input */}
      <div
        className={`relative flex items-center border rounded-lg bg-white transition-colors cursor-pointer ${
          isOpen ? 'border-[#33b864] ring-1 ring-[#33b864]/20' : 'border-gray-200 hover:border-gray-300'
        }`}
        onClick={() => {
          setIsOpen(true);
          setTimeout(() => inputRef.current?.focus(), 0);
        }}
      >
        {value && !isOpen ? (
          // Show selected template
          <div className="flex items-center gap-2 px-3 py-2.5 w-full">
            {getFileTypeIcon(value)}
            <span className="text-sm font-medium text-gray-900 truncate flex-1">
              {value.doc_name}
            </span>
            <Badge variant="secondary" className="text-xs flex-shrink-0">
              {value.category}
            </Badge>
            <button
              onClick={handleClear}
              className="ml-1 p-0.5 rounded-full hover:bg-gray-100 transition-colors"
            >
              <X className="h-3.5 w-3.5 text-gray-400" />
            </button>
          </div>
        ) : (
          // Show search input
          <div className="flex items-center gap-2 px-3 py-0.5 w-full">
            <Search className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <Input
              ref={inputRef}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={placeholder}
              className="border-0 shadow-none focus-visible:ring-0 px-0 h-9 text-sm"
              onFocus={() => setIsOpen(true)}
            />
            {showLoading ? (
              <Loader2 className="h-4 w-4 text-gray-400 animate-spin flex-shrink-0" />
            ) : (
              <ChevronDown className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            )}
          </div>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
          {showLoading ? (
            // Loading state
            <div className="flex items-center justify-center gap-2 py-8 text-gray-500">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm">Loading templates...</span>
            </div>
          ) : error ? (
            // Error state
            <div className="py-6 px-4 text-center">
              <p className="text-sm text-red-500 mb-2">{error}</p>
              <button
                onClick={loadTemplates}
                className="text-xs text-[#33b864] hover:text-[#2a9d54] font-medium"
              >
                Retry
              </button>
            </div>
          ) : filteredTemplates.length === 0 ? (
            // Empty state
            <div className="py-6 px-4 text-center">
              <Library className="h-8 w-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500 mb-1">
                {searchQuery ? 'No templates match your search' : 'No templates available'}
              </p>
              <p className="text-xs text-gray-400 mb-3">
                Upload templates in the Revisr Library to get started
              </p>
              <Link
                to="/revisr/resource-library/documents"
                className="text-xs text-[#33b864] hover:text-[#2a9d54] font-medium"
              >
                Go to Revisr Library →
              </Link>
            </div>
          ) : (
            // Template list
            filteredTemplates.map((template) => {
              const isSelected = value?.id === template.id && value?.doc_name === template.doc_name;

              return (
                <div
                  key={`${template.id}-${template.doc_name}`}
                  className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${
                    isSelected
                      ? 'bg-[#33b864]/5 text-gray-900'
                      : 'hover:bg-gray-50 text-gray-700'
                  }`}
                  onClick={() => handleSelect(template)}
                >
                  {/* File type icon */}
                  {getFileTypeIcon(template)}

                  {/* Template info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{template.doc_name}</p>
                    <p className="text-xs text-gray-400">
                      {template.chunk_count} chunk{template.chunk_count !== 1 ? 's' : ''}
                      {template.file_size ? ` · ${formatFileSize(template.file_size)}` : ''}
                    </p>
                  </div>

                  {/* Category badge */}
                  <Badge variant="secondary" className="text-xs flex-shrink-0">
                    {template.category}
                  </Badge>

                  {/* Selected indicator */}
                  {isSelected && (
                    <Check className="h-4 w-4 text-[#33b864] flex-shrink-0" />
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

// Helper to format file size
const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export default TemplateSelector;
